import { createSlice } from "@reduxjs/toolkit";

import {
  dprListThunk,
  getContactDetailsDPR,
  getQHSEDprData,
  getPersonnelDprListData,
  getDetailedTimeAnalysisData,
  getdailyActivityLogsData,
  getProjectTrackerDprData,
  getDetailsProgressData,
  getDprApplicationDocumentThunk,
  getDprEquipmentData,
  getDprDetails,
  // updateDprData,
  deleteDpr,
} from "redux/Thunks/Dpr";

// Constants
import Constants, { Common } from "utils/Constants";
import dprDataFormat from "utils/Types/Dpr";

const initialValuesOfDprData = {
  reportingPeriod: "",
  date: "",
  dprNumber: "",
  project: "",
  client: "",
  version: "",
  prevVersion: "",
  status: "",
  progressData: {
    dprMembers: [],
    progressSummary: [],
    detailProgress: [],
    last24Hours: "",
    next24Hours: "",
    remarks: "",
    lastSyncTime: "",
    fullProgressSummary: false,
  },
  qhse: {
    kpis: [],
    qhseSummary: [],
    remarks: "",
    lastSyncTime: "",
  },
  applicableDocuments: {
    applicableDocumentsSummary: [],
    remarks: "",
    lastSyncTime: "",
    wantdocumentsection: false,
  },
  timeAnalysis: {
    detailedTimeAnalysis: [],
    dailyActivityLog: [],
    remarks: "",
    lastSyncTime: "",
  },
  personnelList: {
    personnelList: [],
    remarks: "",
    lastSyncTime: "",
  },
  equipmentList: {
    equipmentList: [],
    remarks: "",
    lastSyncTime: "",
  },
  commentsAndSignatures: {
    commentOrRemarks: {
      endClientRepresentative: "",
      clientRepresentative: "",
      reynardRepresentative: "",
    },
    namesAndSignature: {
      endClientRepresentative: "",
      clientRepresentative: "",
      reynardRepresentative: "",
    },
  },
  tabTextColor: {},
};

const initailValueOfTabsForDpr = {
  progressTab: 0,
  qhseTab: 0,
  applicableDocsTab: 0,
  timeAnalysisTab: 0,
  personnelListTab: 0,
  equipmentTab: 0,
  commentAndSignaturesTab: 0,
};

// Every tab's API call is tracked separately so an in-flight or failed call can block saving.
// "idle" means the request has not started yet (e.g. a tab that has not been opened).
const REQUEST_IDLE = "idle";
const initialDataRequests = {
  contact: REQUEST_IDLE,
  tracker: REQUEST_IDLE,
  detail: REQUEST_IDLE,
  qhse: REQUEST_IDLE,
  documents: REQUEST_IDLE,
  timeAnalysis: REQUEST_IDLE,
  dailyLogs: REQUEST_IDLE,
  personnel: REQUEST_IDLE,
  equipment: REQUEST_IDLE,
};

// The first tab (Progress) loads as soon as an open DPR opens, so "idle" there still means loading
const EAGER_REQUEST_KEYS = ["contact", "tracker", "detail"];

const isSuccessResponse = (payload) => payload?.status === true;
// QHSE thunk resolves with the raw HTTP status code instead of the API envelope
const isHttpSuccess = (payload) => payload?.status >= 200 && payload?.status < 300;
// Documents thunk resolves with the whole axios response, the API envelope is in `data`
const isEnvelopeSuccess = (payload) => payload?.data?.status === true;

const initialState = {
  dpr: false,
  allDprVersionData: [],

  displayedDprTabsObj: initailValueOfTabsForDpr,
  isDprDetailReqCompleted: false,
  isLatestDataApiCompleted: false,
  dataRequests: initialDataRequests,

  dprData: initialValuesOfDprData,
  dprList: [],
  dprListpagination: {
    totalRecords: 0,
    completed: [],
  },
  loading: false, // list / tab level loading
  detailLoading: Constants.FULFILLED, // modal/detail loading status
  updatedPersonnelList: [],
};

export const DprSlices = createSlice({
  name: "DPR",
  initialState,
  reducers: {
    openDpr(state) {
      state.dpr = true;
    },
    closeDpr(state) {
      state.dpr = false;
    },
    setProgressData(state, action) {
      state.dprData.progressData = action.payload;
    },
    setQhseRemarks(state, action) {
      state.dprData.qhse.remarks = action.payload;
    },
    setQhseKpis(state, action) {
      state.dprData.qhse.kpis = action.payload || [];
    },
    updateQhseKpiValue(state, action) {
      const { kpiId, field, value } = action.payload;
      state.dprData.qhse.kpis = (state.dprData.qhse.kpis || []).map((kpi) => {
        if (String(kpi.kpiId || kpi[Constants.MONGOOSE_ID]) === String(kpiId)) {
          // Helper function to safely convert value to number
          const toNumber = (val) => {
            if (val === "" || val === null || val === undefined) return 0;
            return Number(val) || 0;
          };

          const updatedKpi = {
            ...kpi,
            [field]: toNumber(value),
          };

          // Auto-calculate total when previous or today changes
          if (field === "previous" || field === "today") {
            const previous = field === "previous" ? toNumber(value) : toNumber(kpi.previous);
            const today = field === "today" ? toNumber(value) : toNumber(kpi.today);
            updatedKpi.total = previous + today;
          }

          return updatedKpi;
        }
        return kpi;
      });
    },
    setApplicableDocumentsRemarks(state, action) {
      state.dprData.applicableDocuments.remarks = action.payload;
    },
    setApplicableDocumentsWantDocumentSection(state, action) {
      state.dprData.applicableDocuments.wantdocumentsection = action.payload;
    },
    setTimeAnalysisRemarks(state, action) {
      state.dprData.timeAnalysis.remarks = action.payload;
    },
    setEquipmentRemarks(state, action) {
      state.dprData.equipmentList.remarks = action.payload;
    },
    setPersonnelListRemarks(state, action) {
      state.dprData.personnelList.remarks = action.payload;
    },
    setCommentOrRemarks(state, action) {
      state.dprData.commentsAndSignatures.commentOrRemarks = action.payload;
    },
    setNamesAndSignature(state, action) {
      state.dprData.commentsAndSignatures.namesAndSignature = action.payload;
    },

    setEditDprStatus(state, action) {
      state.dprData.status = action.payload;
    },

    // Whenever add a new variable please check if it need to be reset at the time of resetDPR
    resetDPR(state) {
      state.dprData = initialValuesOfDprData;
      state.displayedDprTabsObj = initailValueOfTabsForDpr;
      state.isDprDetailReqCompleted = false;
      state.isLatestDataApiCompleted = false;
      state.dataRequests = initialDataRequests;
      state.allDprVersionData = [];
      state.detailLoading = Constants.FULFILLED;
    },
    setDprPersonnelList(state, action) {
      state.dprData.personnelList.personnelList = action.payload;
    },

    updateDisplayedDprTabsObjAtReload(state, action) {
      const { tabKeyName, value } = action.payload;
      state.displayedDprTabsObj = {
        ...initailValueOfTabsForDpr,
        [tabKeyName]: state.displayedDprTabsObj[tabKeyName] + value,
      };
    },

    resetDisplayedDprTabsObj(state) {
      state.displayedDprTabsObj = initailValueOfTabsForDpr;
    },

    updateIsLatestDataApiCompleted(state, action) {
      state.isLatestDataApiCompleted = action.payload;
    },

    updateSyncUpTimeForTabs(state, action) {
      const { dprKeyName, syncTime } = action.payload;
      state.dprData[dprKeyName].lastSyncTime = syncTime;
    },

    updatePersonnelList(state, { payload }) {
      const index = state.updatedPersonnelList.findIndex(
        (item) => item?.indexNum === payload?.indexNum
      );

      if (index !== -1) {
        // Update the existing object if founded.
        state.updatedPersonnelList[index] = payload;
      } else {
        // Add the new object if not founded.
        state.updatedPersonnelList.push(payload);
      }
    },
    updateTabTextColor(state, { payload }) {
      state.dprData.tabTextColor = payload;
    },
  },

  extraReducers: {
    [dprListThunk.pending]: (state) => {
      state.loading = Constants.PENDING;
    },
    [dprListThunk.fulfilled]: (state, { payload, meta }) => {
      const { dprs = [], totalRecords } = payload?.data || {};

      state.loading = Constants.FULFILLED;

      // Get pagination info from the meta argument
      const params = new URLSearchParams(meta.arg);
      const page = parseInt(params.get("page"), 10);
      const perPage = parseInt(params.get("perPage"), 10);

      // If dprs array is empty, initialize it with the required format
      const dprsArrList =
        page === 0
          ? Array.from({ length: totalRecords }, () => ({ ...dprDataFormat }))
          : [...state.dprList];

      const startIndex = page * perPage;
      dprsArrList.splice(startIndex, perPage, ...dprs);

      // Update state with new data
      state.dprList = dprsArrList;
      state.dprListpagination = {
        totalRecords,
        completed: page === 0 ? [page] : [...state.dprListpagination.completed, page],
      };
    },

    [dprListThunk.rejected]: (state) => {
      state.loading = Constants.REJECTED;
    },

    [getDprDetails.pending]: (state) => {
      state.detailLoading = Constants.PENDING;
      state.isDprDetailReqCompleted = false;
    },
    [getDprDetails.fulfilled]: (state, { payload }) => {
      state.detailLoading = Constants.FULFILLED;

      // First putting the current version data to dpr data
      const currentVersionData = payload?.data?.currentVersionData || {};
      state.dprData = { ...initialValuesOfDprData, ...currentVersionData };

      // then updating the dpr data with response data
      state.dprData = { ...state.dprData, version: payload?.data?.version };
      state.dprData.qhse = {
        ...state.dprData.qhse,
        kpis: state.dprData?.qhse?.kpis || [],
      };
      state.dprData.dprNumber = payload?.data?.dprNo;
      state.dprData.date = payload?.data?.dprDate;
      state.dprData.project = payload?.data?.project; // Saving Id here
      state.dprData.client = payload?.data?.client;
      state.dprData.status = payload?.data?.status;
      state.dprData.prevVersion = payload?.data?.prevVersion || payload?.data?.version;

      state.allDprVersionData = payload?.data?.allDprVersionData;
      state.isDprDetailReqCompleted = true;

      if (!payload?.data?.currentVersionData && payload?.data?.allDprVersionData?.length > 0) {
        const firstVersionData = payload?.data?.allDprVersionData?.[0] || {};
        state.dprData = { ...initialValuesOfDprData, ...firstVersionData };
        state.dprData = { ...state.dprData, version: payload?.data?.version };
        state.dprData.qhse = {
          ...state.dprData.qhse,
          kpis: state.dprData?.qhse?.kpis || [],
        };
        state.dprData.dprNumber = payload?.data?.dprNo;
        state.dprData.date = payload?.data?.dprDate;
        // state.dprData.project = payload?.data?.project; // Saving Id here
        state.dprData.status = payload?.data?.status;
        state.dprData.prevVersion = payload?.data?.prevVersion || payload?.data?.version;
      }
    },
    [getDprDetails.rejected]: (state) => {
      state.isDprDetailReqCompleted = true;
      state.detailLoading = Constants.REJECTED;
    },

    // Tab-1 Progress Tab Request Starts here
    [getContactDetailsDPR.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.contact = Constants.PENDING;
    },
    [getContactDetailsDPR.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      if (!isSuccessResponse(payload)) {
        state.dataRequests.contact = Constants.REJECTED;
        return;
      }
      state.dataRequests.contact = Constants.FULFILLED;
      const dprMemberData = payload?.data || {};

      state.dprData.reportingPeriod = dprMemberData.reportingPeriod;
      state.dprData.date = dprMemberData.date;
      state.dprData.client = dprMemberData.client;
      state.dprData.project = dprMemberData.project; // It saving Name here
      state.dprData.dprNumber = dprMemberData.dprNumber;
      state.dprData.version = dprMemberData.version;
      state.dprData.status = dprMemberData.status;
      state.dprData.progressData = {
        ...state.dprData.progressData,
        dprMembers: dprMemberData.dprMembers || [],
      };
    },
    [getContactDetailsDPR.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.contact = Constants.REJECTED;
    },
    [getProjectTrackerDprData.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.tracker = Constants.PENDING;
    },
    [getProjectTrackerDprData.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      // A failed response must never replace the existing progress summary
      if (!isSuccessResponse(payload)) {
        state.dataRequests.tracker = Constants.REJECTED;
        return;
      }
      state.dataRequests.tracker = Constants.FULFILLED;
      state.dprData.progressData = {
        ...state.dprData.progressData,
        progressSummary: payload?.data || [],
      };
    },
    [getProjectTrackerDprData.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.tracker = Constants.REJECTED;
    },

    [getDetailsProgressData.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.detail = Constants.PENDING;
    },
    [getDetailsProgressData.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      if (!isSuccessResponse(payload)) {
        state.dataRequests.detail = Constants.REJECTED;
        return;
      }
      state.dataRequests.detail = Constants.FULFILLED;
      state.dprData.progressData = {
        ...state.dprData.progressData,
        detailProgress: payload?.data || [],
      };
    },
    [getDetailsProgressData.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.detail = Constants.REJECTED;
    },
    // Tab-1 Progress tab API Request Ends here

    // Tab-2 QHSE Tab API Request Starts here
    [getQHSEDprData.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.qhse = Constants.PENDING;
    },
    [getQHSEDprData.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      if (!isHttpSuccess(payload)) {
        state.dataRequests.qhse = Constants.REJECTED;
        return;
      }
      state.dataRequests.qhse = Constants.FULFILLED;
      state.dprData.qhse.kpis = payload?.data?.kpis || [];
      state.dprData.qhse.qhseSummary = payload?.data?.qhseSummary || [];
      state.dprData.qhse.remarks =
        payload?.data?.remarks !== undefined ? payload.data.remarks : state.dprData.qhse.remarks;
    },
    [getQHSEDprData.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.qhse = Constants.REJECTED;
    },
    // Tab-2 QHSE Tab API Request Ends here

    // Tab-3 Applicable Documents Tab API Request Starts here
    [getDprApplicationDocumentThunk.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.documents = Constants.PENDING;
    },
    [getDprApplicationDocumentThunk.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      if (!isEnvelopeSuccess(payload)) {
        state.dataRequests.documents = Constants.REJECTED;
        return;
      }
      state.dataRequests.documents = Constants.FULFILLED;
      state.dprData.applicableDocuments.applicableDocumentsSummary = payload?.data?.data;
    },
    [getDprApplicationDocumentThunk.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.documents = Constants.REJECTED;
    },
    // Tab-3 Applicable Documents Tab API Request Ends here

    // Tab-4 Time Analysis Tab API Request Starts here
    [getDetailedTimeAnalysisData.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.timeAnalysis = Constants.PENDING;
    },
    [getDetailedTimeAnalysisData.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      if (!isSuccessResponse(payload)) {
        state.dataRequests.timeAnalysis = Constants.REJECTED;
        return;
      }
      state.dataRequests.timeAnalysis = Constants.FULFILLED;
      state.dprData.timeAnalysis.detailedTimeAnalysis = payload?.data;
    },
    [getDetailedTimeAnalysisData.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.timeAnalysis = Constants.REJECTED;
    },

    [getdailyActivityLogsData.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.dailyLogs = Constants.PENDING;
    },
    [getdailyActivityLogsData.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      if (!isSuccessResponse(payload)) {
        state.dataRequests.dailyLogs = Constants.REJECTED;
        return;
      }
      state.dataRequests.dailyLogs = Constants.FULFILLED;
      state.dprData.timeAnalysis.dailyActivityLog = payload?.data;
    },
    [getdailyActivityLogsData.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.dailyLogs = Constants.REJECTED;
    },
    // Tab-4 Time Analysis Tab API Request Starts here

    // Tab-5 ==> Personnel Tab API Request Starts here
    [getPersonnelDprListData.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.personnel = Constants.PENDING;
    },
    [getPersonnelDprListData.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      if (!isSuccessResponse(payload)) {
        state.dataRequests.personnel = Constants.REJECTED;
        return;
      }
      state.dataRequests.personnel = Constants.FULFILLED;
      state.dprData.personnelList.personnelList = payload?.data;
    },
    [getPersonnelDprListData.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.personnel = Constants.REJECTED;
    },
    // Tab-5 Personnel Tab API Request Ends here

    // Tab-6 Equipment Tab API Request Starts here
    [getDprEquipmentData.pending]: (state) => {
      state.loading = Constants.PENDING;
      state.dataRequests.equipment = Constants.PENDING;
    },
    [getDprEquipmentData.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;

      if (!isSuccessResponse(payload)) {
        state.dataRequests.equipment = Constants.REJECTED;
        return;
      }
      state.dataRequests.equipment = Constants.FULFILLED;
      state.dprData.equipmentList.equipmentList = payload?.data;
    },
    [getDprEquipmentData.rejected]: (state) => {
      state.loading = Constants.REJECTED;
      state.dataRequests.equipment = Constants.REJECTED;
    },
    // Tab-6 Equipment Tab API Request Ends here
    // Delete DPR
    [deleteDpr.pending]: (state) => {
      state.loading = Constants.PENDING;
    },
    [deleteDpr.fulfilled]: (state, { payload }) => {
      state.loading = Constants.FULFILLED;
      state.dprList = state?.dprList?.filter(
        (item) => item[Constants.MONGOOSE_ID] !== payload?.data?.data[Constants.MONGOOSE_ID]
      );
    },
    [deleteDpr.rejected]: (state) => {
      state.loading = Constants.REJECTED;
    },
  },
});

// Status a tab's table should show: an open DPR whose first tab has not fetched yet counts as loading
const getDataRequestStatus = (state, key) => {
  const { detailLoading, dataRequests, dprData } = state.dprs;
  const status = dataRequests[key];

  if (detailLoading === Constants.PENDING) return Constants.PENDING;
  if (status === REQUEST_IDLE) {
    const isEagerAndOpen =
      EAGER_REQUEST_KEYS.includes(key) && dprData?.status?.toLowerCase() === Common.DPR_STATUS_OPEN;
    return isEagerAndOpen ? Constants.PENDING : Constants.FULFILLED;
  }
  return status;
};

const createRequestStatusSelector = (key) => (state) => getDataRequestStatus(state, key);

export const selectProgressContactStatus = createRequestStatusSelector("contact");
export const selectProgressTrackerStatus = createRequestStatusSelector("tracker");
export const selectProgressDetailStatus = createRequestStatusSelector("detail");
export const selectQhseStatus = createRequestStatusSelector("qhse");
export const selectDocumentsStatus = createRequestStatusSelector("documents");
export const selectTimeAnalysisStatus = createRequestStatusSelector("timeAnalysis");
export const selectDailyLogsStatus = createRequestStatusSelector("dailyLogs");
export const selectPersonnelStatus = createRequestStatusSelector("personnel");
export const selectEquipmentStatus = createRequestStatusSelector("equipment");

// Save / submit actions persist the whole dprData, so they stay blocked until it is fully loaded
export const selectIsDprSaveBlocked = (state) => {
  const { detailLoading, dataRequests, dprData } = state.dprs;

  if (!dprData?.status || detailLoading !== Constants.FULFILLED) return true;

  return Object.keys(dataRequests).some(
    (key) => getDataRequestStatus(state, key) !== Constants.FULFILLED
  );
};

export const {
  openDpr,
  closeDpr,
  setProgressData,
  setQhseRemarks,
  setQhseKpis,
  updateQhseKpiValue,
  setApplicableDocumentsRemarks,
  setApplicableDocumentsWantDocumentSection,
  setTimeAnalysisRemarks,
  setEquipmentRemarks,
  setPersonnelListRemarks,
  setCommentOrRemarks,
  setNamesAndSignature,
  setEditDprStatus,
  resetDPR,
  setDprPersonnelList,
  updateDisplayedDprTabsObjAtReload,
  resetDisplayedDprTabsObj,
  updateIsLatestDataApiCompleted,
  updateSyncUpTimeForTabs,
  updatePersonnelList,
  updateTabTextColor,
} = DprSlices.actions;

export default DprSlices.reducer;
